import React, { useState, useEffect } from 'react';
import { COMPANY_NAME_EN } from './constants';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage, Language } from './i18n/LanguageContext';

interface NavbarProps {
  onAboutClick: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({ onAboutClick }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLanguage = () => {
    setLanguage(language === 'zh' ? 'en' : 'zh');
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled ? 'bg-black/80 backdrop-blur-md py-4' : 'bg-transparent py-6'
      }`}
    >
      <div className="w-full px-8 md:px-12 lg:px-20 flex justify-between items-center">
        {/* Left: Logo */}
        <div className="flex items-center gap-2  group">
          <img 
            src="/images/logo.png" 
            alt="Wyndme Logo" 
            className="h-10 md:h-12 w-auto"
          />
        </div>

        {/* Desktop Right: About Us + Language Toggle */}
        <div className="hidden md:flex items-center space-x-8">
           <button 
             onClick={onAboutClick}
             className="text-sm uppercase tracking-[0.2em] text-gray-300 hover:text-white transition-colors duration-300 border-b border-transparent hover:border-white pb-1"
           >
             {t.nav.about}
           </button>
           <button 
             onClick={toggleLanguage}
             className="flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-gray-300 hover:text-white transition-colors duration-300 border-b border-transparent hover:border-white pb-1"
           >
             <Globe size={16} />
             {language === 'zh' ? 'EN' : '中文'}
           </button>
        </div>

        {/* Mobile Menu Toggle */}
        <div className="md:hidden">
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-white p-2">
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="absolute top-full left-0 w-full bg-black/95 backdrop-blur-lg border-t border-gray-800 p-6 flex flex-col items-center space-y-6 md:hidden">
          <button 
            onClick={() => {
              onAboutClick();
              setMobileMenuOpen(false);
            }}
            className="text-lg uppercase tracking-widest text-white"
          >
            {t.nav.about}
          </button>
          <button 
            onClick={() => {
              toggleLanguage();
              setMobileMenuOpen(false);
            }}
            className="text-lg uppercase tracking-widest text-white flex items-center gap-2"
          >
            <Globe size={18} />
            {language === 'zh' ? 'English' : '中文'}
          </button>
        </div>
      )}
    </nav>
  );
};
